import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import { Plus, Send, CalendarClock, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pct = (v) => (v == null ? 'N/A' : `${(v * 100).toFixed(1)}%`);

const TEMPLATES = {
  newsletter: {
    name: 'IKWorx Newsletter',
    html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;">
  <div style="background:#0f172a;padding:24px;text-align:left;"><h1 style="color:#eab308;margin:0;font-size:22px;">IKWorx</h1><p style="color:#94a3b8;margin:4px 0 0;font-size:13px;">Innovative Knowledge Worx</p></div>
  <div style="padding:28px 24px;">
    <h2 style="color:#0f172a;font-size:19px;margin:0 0 12px;">Your headline here</h2>
    <p style="color:#475569;font-size:14px;line-height:1.6;">Write your newsletter content here. Share company updates, programme news and industry insights with your audience.</p>
    <a href="https://ikworx.co.za" style="display:inline-block;margin-top:16px;background:#eab308;color:#0f172a;text-decoration:none;padding:10px 22px;border-radius:6px;font-weight:bold;font-size:14px;">Read More</a>
  </div>
  <div style="background:#f8fafc;padding:16px 24px;font-size:11px;color:#94a3b8;">IKWorx (Pty) Ltd — South Africa<br/>You are receiving this because you opted in. <a href="*|UNSUB|*" style="color:#64748b;">Unsubscribe</a></div>
</div>`,
  },
  promotion: {
    name: 'Programme Promotion',
    html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;">
  <div style="background:#eab308;padding:28px 24px;"><h1 style="color:#0f172a;margin:0;font-size:24px;">New Programme Alert</h1></div>
  <div style="padding:28px 24px;">
    <h2 style="color:#0f172a;font-size:18px;margin:0 0 12px;">Describe your training programme or service</h2>
    <p style="color:#475569;font-size:14px;line-height:1.6;">Highlight the benefits, dates, accreditation and who should apply. Keep it short and action-driven.</p>
    <a href="https://ikworx.co.za" style="display:inline-block;margin-top:16px;background:#0f172a;color:#ffffff;text-decoration:none;padding:12px 26px;border-radius:6px;font-weight:bold;font-size:14px;">Apply Now</a>
  </div>
  <div style="background:#f8fafc;padding:16px 24px;font-size:11px;color:#94a3b8;">IKWorx (Pty) Ltd — South Africa<br/><a href="*|UNSUB|*" style="color:#64748b;">Unsubscribe</a></div>
</div>`,
  },
  announcement: {
    name: 'Simple Announcement',
    html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;border:1px solid #e2e8f0;">
  <div style="padding:28px 24px;">
    <p style="color:#eab308;font-weight:bold;font-size:12px;letter-spacing:1px;margin:0 0 8px;">IKWORX ANNOUNCEMENT</p>
    <h2 style="color:#0f172a;font-size:20px;margin:0 0 12px;">Your announcement title</h2>
    <p style="color:#475569;font-size:14px;line-height:1.6;">Write your announcement here.</p>
  </div>
  <div style="background:#0f172a;padding:16px 24px;font-size:11px;color:#94a3b8;">IKWorx (Pty) Ltd — <a href="*|UNSUB|*" style="color:#eab308;">Unsubscribe</a></div>
</div>`,
  },
};

const EMPTY = { list_id: '', title: '', subject: '', from_name: 'IKWorx', reply_to: '', template: 'newsletter', html_content: TEMPLATES.newsletter.html, schedule_time: '' };

export const CampaignBuilder = ({ audiences, connected, onDone }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [confirm, setConfirm] = useState(null); // 'send' | 'schedule' | 'draft'
  const [busy, setBusy] = useState(false);

  const create = async (mode) => {
    if (!form.list_id) return toast.error('Select an audience');
    if (!form.title.trim() || !form.subject.trim()) return toast.error('Title and subject are required');
    if (!form.reply_to.trim()) return toast.error('Reply-to email is required');
    if (mode === 'schedule' && !form.schedule_time) return toast.error('Pick a schedule date & time');
    setBusy(true);
    try {
      const payload = {
        list_id: form.list_id, title: form.title, subject: form.subject,
        from_name: form.from_name, reply_to: form.reply_to, html_content: form.html_content,
        schedule_time: mode === 'schedule' ? new Date(form.schedule_time).toISOString() : null,
      };
      const res = await axios.post(`${API}/mailchimp/campaigns`, payload);
      if (mode === 'send') {
        await axios.post(`${API}/mailchimp/campaigns/${res.data.id}/actions/send`);
        toast.success('Campaign is sending via Mailchimp');
      } else {
        toast.success(mode === 'schedule' ? 'Campaign scheduled in Mailchimp' : 'Draft created in Mailchimp');
      }
      setOpen(false); setForm(EMPTY); setConfirm(null);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mailchimp rejected the campaign');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" className="gap-1.5" disabled={!connected} onClick={() => setOpen(true)} data-testid="new-campaign-btn">
        <Plus className="h-4 w-4" /> New Campaign
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="campaign-builder-dialog">
          <DialogHeader><DialogTitle>New Email Campaign</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Internal title *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="campaign-title-input" /></div>
            <div className="space-y-1"><Label>Subject line *</Label>
              <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} data-testid="campaign-subject-input" /></div>
            <div className="space-y-1"><Label>From name</Label>
              <Input value={form.from_name} onChange={e => setForm({ ...form, from_name: e.target.value })} /></div>
            <div className="space-y-1"><Label>Reply-to email *</Label>
              <Input type="email" value={form.reply_to} onChange={e => setForm({ ...form, reply_to: e.target.value })} data-testid="campaign-replyto-input" /></div>
            <div className="space-y-1"><Label>Audience *</Label>
              <Select value={form.list_id} onValueChange={v => setForm({ ...form, list_id: v })}>
                <SelectTrigger data-testid="campaign-audience-select"><SelectValue placeholder="Select audience" /></SelectTrigger>
                <SelectContent>{audiences.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label>Template</Label>
              <Select value={form.template} onValueChange={v => setForm({ ...form, template: v, html_content: TEMPLATES[v].html })}>
                <SelectTrigger data-testid="campaign-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TEMPLATES).map(([k, t]) => <SelectItem key={k} value={k}>{t.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1">
            <Label>Email HTML (edit freely — unsubscribe link is included)</Label>
            <Textarea rows={6} className="font-mono text-xs" value={form.html_content}
              onChange={e => setForm({ ...form, html_content: e.target.value })} data-testid="campaign-html-input" />
          </div>
          <div className="rounded-lg border overflow-hidden bg-white">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-3 py-1.5 border-b bg-muted">Preview</p>
            <iframe title="preview" srcDoc={form.html_content} className="w-full h-56 pointer-events-none" />
          </div>
          <div className="space-y-1">
            <Label>Schedule for (optional)</Label>
            <Input type="datetime-local" value={form.schedule_time} onChange={e => setForm({ ...form, schedule_time: e.target.value })} data-testid="campaign-schedule-input" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={busy} onClick={() => create('draft')} data-testid="campaign-draft-btn">Save Draft</Button>
            <Button variant="outline" disabled={busy || !form.schedule_time} onClick={() => setConfirm('schedule')} data-testid="campaign-schedule-btn">
              <CalendarClock className="h-4 w-4 mr-1" /> Schedule
            </Button>
            <Button disabled={busy} onClick={() => setConfirm('send')} data-testid="campaign-send-btn">
              <Send className="h-4 w-4 mr-1" /> Send Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="campaign-confirm-dialog">
          <DialogHeader><DialogTitle>{confirm === 'send' ? 'Send to real subscribers?' : 'Schedule this campaign?'}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {confirm === 'send'
              ? 'This will immediately email everyone in the selected Mailchimp audience. This cannot be undone.'
              : `Mailchimp will send this campaign at ${form.schedule_time ? new Date(form.schedule_time).toLocaleString('en-ZA') : ''}.`}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button disabled={busy} onClick={() => create(confirm)} data-testid="campaign-confirm-btn">
              {busy ? 'Working…' : confirm === 'send' ? 'Yes, send now' : 'Yes, schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const MarketingCalendar = ({ campaigns }) => {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const year = month.getFullYear(), m = month.getMonth();
  const firstDow = (new Date(year, m, 1).getDay() + 6) % 7; // Monday first
  const daysInMonth = new Date(year, m + 1, 0).getDate();
  const byDay = {};
  campaigns.filter(c => c.send_time).forEach(c => {
    const d = new Date(c.send_time);
    if (d.getFullYear() === year && d.getMonth() === m) (byDay[d.getDate()] = byDay[d.getDate()] || []).push(c);
  });
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  return (
    <Card data-testid="marketing-calendar">
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base">{month.toLocaleString('en-ZA', { month: 'long', year: 'numeric' })}</CardTitle>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setMonth(new Date(year, m - 1, 1))} data-testid="calendar-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setMonth(new Date(year, m + 1, 1))} data-testid="calendar-next-btn"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-muted-foreground mb-1">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, i) => (
            <div key={i} className={`min-h-[72px] rounded-lg border p-1 ${day ? 'bg-card' : 'bg-transparent border-transparent'}`}>
              {day && <>
                <p className="text-[11px] text-muted-foreground">{day}</p>
                {(byDay[day] || []).map(c => (
                  <div key={c.id} className={`mt-0.5 rounded px-1 py-0.5 text-[10px] truncate ${c.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} title={c.title || c.subject}>
                    {c.title || c.subject}
                  </div>
                ))}
              </>}
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">Green = sent, amber = scheduled (from Mailchimp)</p>
      </CardContent>
    </Card>
  );
};

export const CampaignReports = ({ campaigns }) => {
  const [reports, setReports] = useState({});
  const fetchReport = async (id) => {
    try {
      const r = await axios.get(`${API}/mailchimp/campaigns/${id}/report`);
      setReports(prev => ({ ...prev, [id]: r.data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Report unavailable (campaign may not be sent yet)');
    }
  };
  const sent = campaigns.filter(c => c.status === 'sent' || c.emails_sent > 0);
  return (
    <Card data-testid="campaign-reports">
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Campaign</TableHead><TableHead>Sent</TableHead><TableHead>Opens</TableHead>
              <TableHead>Clicks</TableHead><TableHead>Bounces</TableHead><TableHead>Unsubs</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sent.map(c => {
              const r = reports[c.id];
              return (
                <TableRow key={c.id}>
                  <TableCell className="text-sm font-medium">{c.title || c.subject}</TableCell>
                  <TableCell className="font-mono text-sm">{r?.emails_sent ?? c.emails_sent ?? 'N/A'}</TableCell>
                  <TableCell className="font-mono text-sm">{r ? `${r.opens?.unique_opens ?? 'N/A'} (${pct(r.opens?.open_rate)})` : '—'}</TableCell>
                  <TableCell className="font-mono text-sm">{r ? `${r.clicks?.unique_clicks ?? 'N/A'} (${pct(r.clicks?.click_rate)})` : '—'}</TableCell>
                  <TableCell className="font-mono text-sm">{r ? ((r.bounces?.hard_bounces ?? 0) + (r.bounces?.soft_bounces ?? 0)) : '—'}</TableCell>
                  <TableCell className="font-mono text-sm">{r ? (r.unsubscribes ?? 'N/A') : '—'}</TableCell>
                  <TableCell><Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => fetchReport(c.id)} data-testid={`fetch-report-${c.id}`}><BarChart3 className="h-3 w-3" /> Load</Button></TableCell>
                </TableRow>
              );
            })}
            {sent.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No sent campaigns yet — reports appear after your first send</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export const StatusBadge = ({ status }) => {
  const tones = { sent: 'bg-emerald-100 text-emerald-700', schedule: 'bg-amber-100 text-amber-700', scheduled: 'bg-amber-100 text-amber-700', save: 'bg-slate-100 text-slate-600', sending: 'bg-blue-100 text-blue-700', paused: 'bg-red-100 text-red-600' };
  return <Badge className={tones[status] || 'bg-slate-100 text-slate-600'}>{status === 'save' ? 'draft' : status}</Badge>;
};
